import { postSendAIChatLogEvent } from '$tablet-app/src/app/logging/functions';
import {
  chatResponseIsDirectMatch,
  postChatMessage,
} from '@akin-home/chat/frontend';
import { useCommandStore } from '@akin-home/commands-react';
import { merge } from '@akin-home/objects';
import * as Location from 'expo-location';
import { Platform } from 'react-native';
import {
  createInputChatItem,
  createOutputTextItem,
} from '../../ChatItem/functions';
import {
  filterInvalidChatItems,
  isOutputChatItem,
  postProcessChatItem,
} from '../../functions';
import { AppStateSlice } from './../../../stores/app_state';
import {
  getUserChatAppID,
  getUserDataForChatContext,
} from './../../../users/functions';
import { ChatStore } from './types';

export const createChatStore: AppStateSlice<ChatStore> = (set, get) => ({
  waiting_for_response: false,
  chat_dialog_visible: false,

  showChatDialog() {
    set({ chat_dialog_visible: true });
  },
  hideChatDialog() {
    set({ chat_dialog_visible: false });
  },
  setWaitingForResponse(waiting_for_response) {
    set({ waiting_for_response });
  },
  setSessionID(session_id) {
    set({ session_id });
  },
  clearDisplayedChatItem() {
    set({ displayed_chat_item_idx: undefined });
  },
  clearDisplayedDynamicItem() {
    set({ displayed_dynamic_chat_item: undefined });
  },

  async sendChatMessage(message, options) {
    const {
      current_user: user,
      session_id,
      send_chat_message_throttle,
    } = get();

    if (!user || send_chat_message_throttle) return;

    const application_id = getUserChatAppID(user);

    /** Due to some weirdness with ios STT, we need to throttle the chat sending on ios */
    if (Platform.OS === 'ios') {
      set({
        send_chat_message_throttle: setTimeout(() => {
          set({ send_chat_message_throttle: undefined });
        }, 3000),
      });
    }

    if (!options?.silent) {
      get().addChatItem(createInputChatItem(message));

      postSendAIChatLogEvent({
        user_id: user.id,
        data: {
          session_id,
          application_id,
        },
      });

      set({ waiting_for_response: true });
    }

    let location: any = undefined;

    const allowed_location_permissions =
      (await Location.getForegroundPermissionsAsync()).status === 'granted';

    if (allowed_location_permissions) {
      location = await Location.getLastKnownPositionAsync();

      if (!location) {
        location = await Location.getCurrentPositionAsync();
      }
    }

    const location_context = location
      ? {
          location: {
            latitude: location?.coords.latitude,
            longitude: location?.coords.longitude,
          },
        }
      : {};

    const context = get().evaluateChatContext(
      merge(
        { user: { ...getUserDataForChatContext(user), ...location_context } },
        { platform: Platform.OS },
        options?.context
      )
    );

    try {
      const res = await postChatMessage({
        message,
        message_type: options?.send_as_label ? 'labeled_intent' : 'text',
        username: `${user.username}#${user.id}`,
        use_generative_ai: options?.direct_only
          ? undefined
          : user.use_generative_ai,
        session_id,
        application_id,
        context,
      });

      set({ waiting_for_response: false });

      if (!res.success) {
        /** Not successful, just return */
        if (!options?.silent) {
          get().addChatItem(
            createOutputTextItem(
              'Sorry, I am not able to respond at the moment. Please try again soon.'
            )
          );
        }

        return;
      }

      /** Update the session ID */
      set({ session_id: res.session_id });

      /** If we only want a direct match, ignore anything else */
      if (options?.direct_only && !chatResponseIsDirectMatch(res)) {
        return;
      }

      const { body: items } = res;

      /** Filter out the valid chat items to be posted to the chat */
      const valid_chat_items = filterInvalidChatItems(items);

      if (valid_chat_items.length) {
        const item = valid_chat_items[0];

        get().addChatItem(
          postProcessChatItem({ ...item, timestamp: new Date().toISOString() })
        );
      }

      /** Dispatch commands for returned chat items */
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (item.commands?.length) {
            useCommandStore.getState().dispatch(item.commands);
          }
        });
      } else if (items.commands?.length) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useCommandStore.getState().dispatch(items.commands);
      }
    } catch (ex) {
      set({ waiting_for_response: false });
    }
  },

  async sendChatBackgroundMessage(message, options) {
    get().sendChatMessage(message, {
      silent: true,
      direct_only: true,
      send_as_label: true,
      ...options,
    });
  },

  async selectChatOption(input, data) {
    if (data?.idx !== undefined) {
      set((state) => {
        const item = state.chat_items[data.idx as number];

        if (item && isOutputChatItem(item)) {
          const option = item.data?.options?.find(
            (i) => i.input.value === input
          );

          if (option) {
            option.selected = true;
          }
        }
      });
    }

    get().sendChatMessage(input, { silent: true });
  },

  showFocusedChat() {
    set({ focused_chat_visible: true });
  },
  hideFocusedChat() {
    set({ focused_chat_visible: false });
  },
});
